/**
 * Generic Auth User interface that abstracts away provider-specific user properties
 * This provides a common interface for user data across different auth providers
 */
export interface AuthUser {
  id: string;
  email: string | null;
  email_confirmed_at?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  user_metadata?: Record<string, any>;
  app_metadata?: Record<string, any>;
  [key: string]: unknown; // Allow for provider-specific properties
}

/**
 * Generic Auth Session interface that abstracts away provider-specific session properties
 * This provides a common interface for session data across different auth providers
 */
export interface AuthSession {
  access_token: string;
  refresh_token?: string;
  expires_at: number;
  user?: AuthUser;
  [key: string]: unknown; // Allow for provider-specific properties
}

/**
 * Response structure for authentication operations
 */
export interface AuthResponse {
  data: {
    user: AuthUser | null;
    session: AuthSession | null;
  };
  error: Error | null;
}

/**
 * Base interface for all authentication providers
 * Contains only the essential methods that all providers must implement
 */
export interface BaseAuthProvider {
  /**
   * Sign out the current user
   */
  signOut(): Promise<{ error: Error | null }>;

  /**
   * Get the current user
   */
  getUser(): Promise<{ data: { user: AuthUser | null } }>;

  /**
   * Get the current session
   */
  getSession(): Promise<{ data: { session: AuthSession | null } }>;

  /**
   * Refresh the current session
   */
  refreshSession(): Promise<AuthResponse>;

  /**
   * Listen for auth state changes
   */
  onAuthStateChange(callback: (event: string, session: AuthSession | null) => void): unknown;

  /**
   * Get provider name
   */
  getProviderName(): string;
}

/**
 * Interface for providers that support email/password authentication
 */
export interface EmailPasswordAuthProvider extends BaseAuthProvider {
  /**
   * Sign in with email and password
   */
  signInWithPassword(email: string, password: string): Promise<AuthResponse>;

  /**
   * Sign up with email and password
   */
  signUpWithPassword(email: string, password: string): Promise<AuthResponse>;
}

/**
 * Interface for providers that support OAuth authentication
 */
export interface OAuthAuthProvider extends BaseAuthProvider {
  /**
   * Sign in with Google OAuth
   */
  signInWithGoogle(): Promise<AuthResponse>;

  /**
   * Sign in with Microsoft OAuth
   */
  signInWithMicrosoft(): Promise<AuthResponse>;

  /**
   * Handle OAuth redirect result (optional - only for redirect-based auth flows)
   */
  handleRedirectResult?(): Promise<AuthResponse>;
}

/**
 * Interface for providers that support both email/password and OAuth authentication
 */
export interface FullAuthProvider extends EmailPasswordAuthProvider, OAuthAuthProvider {
  // Inherits all methods from both interfaces
}

/**
 * Union type representing any valid authentication provider
 * This allows for flexible provider implementations while maintaining type safety
 */
export type AuthProvider = FullAuthProvider | EmailPasswordAuthProvider | OAuthAuthProvider;

/**
 * Type guard to check if a provider supports email/password authentication
 */
export function supportsEmailPassword(
  provider: AuthProvider,
): provider is EmailPasswordAuthProvider | FullAuthProvider {
  return 'signInWithPassword' in provider && 'signUpWithPassword' in provider;
}

/**
 * Type guard to check if a provider supports OAuth authentication
 */
export function supportsOAuth(
  provider: AuthProvider,
): provider is OAuthAuthProvider | FullAuthProvider {
  return 'signInWithGoogle' in provider || 'signInWithMicrosoft' in provider;
}

/**
 * Type guard to check if a provider supports redirect results
 */
export function supportsRedirectResult(
  provider: AuthProvider,
): provider is AuthProvider & { handleRedirectResult(): Promise<AuthResponse> } {
  return 'handleRedirectResult' in provider && typeof provider.handleRedirectResult === 'function';
}
